'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { SectionShell } from '@/components/layout/SectionShell';
import { TWIBBON_LOGOS_BASE } from '@/lib/images';

// ---------------------------------------------------------------------------
// SVG icon components (cross-device consistent, replaces emoji)
// ---------------------------------------------------------------------------

function IconCamera({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function IconDownload({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconShare({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function IconCopy({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IconRefresh({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

// Platform SVG icons
function IconIgStory({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
}

function IconIgPost({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function IconIgPortrait({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <circle cx="9" cy="9" r="1.5" />
      <polyline points="20 16 15 11 4 22" />
    </svg>
  );
}

function IconWhatsApp({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function IconTikTok({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.19V12a4.85 4.85 0 01-5.58-2.19V6.69h5.58z" />
    </svg>
  );
}

const PLATFORM_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'ig-story': IconIgStory,
  'ig-square': IconIgPost,
  'ig-portrait': IconIgPortrait,
  'whatsapp': IconWhatsApp,
  'tiktok': IconTikTok,
};

// ---------------------------------------------------------------------------
// Frame definitions
// ---------------------------------------------------------------------------

interface TwibbonFrame {
  id: string;
  label: string;
  /** University abbreviation (uppercase) */
  abbrev: string;
  /** Full university name for display on frame */
  fullName: string;
  /** Primary color hex */
  primaryColor: string;
  /** Secondary color hex */
  secondaryColor: string;
}

const UNIVERSITY_FRAMES: TwibbonFrame[] = [
  { id: 'ui', label: 'Universitas Indonesia', abbrev: 'UI', fullName: 'Universitas Indonesia', primaryColor: '#F2C94C', secondaryColor: '#1E3A5F' },
  { id: 'ugm', label: 'Universitas Gadjah Mada', abbrev: 'UGM', fullName: 'Universitas Gadjah Mada', primaryColor: '#003366', secondaryColor: '#F4B400' },
  { id: 'itb', label: 'Institut Teknologi Bandung', abbrev: 'ITB', fullName: 'Institut Teknologi Bandung', primaryColor: '#003D7C', secondaryColor: '#FFFFFF' },
  { id: 'its', label: 'Institut Teknologi Sepuluh Nopember', abbrev: 'ITS', fullName: 'Institut Teknologi Sepuluh Nopember', primaryColor: '#00529B', secondaryColor: '#FFD700' },
  { id: 'unair', label: 'Universitas Airlangga', abbrev: 'UNAIR', fullName: 'Universitas Airlangga', primaryColor: '#003087', secondaryColor: '#FFD100' },
  { id: 'ipb', label: 'IPB University', abbrev: 'IPB', fullName: 'IPB University', primaryColor: '#006633', secondaryColor: '#FFD700' },
  { id: 'undip', label: 'Universitas Diponegoro', abbrev: 'UNDIP', fullName: 'Universitas Diponegoro', primaryColor: '#003399', secondaryColor: '#FFFFFF' },
  { id: 'ub', label: 'Universitas Brawijaya', abbrev: 'UB', fullName: 'Universitas Brawijaya', primaryColor: '#003366', secondaryColor: '#CC0000' },
  { id: 'unpad', label: 'Universitas Padjadjaran', abbrev: 'UNPAD', fullName: 'Universitas Padjadjaran', primaryColor: '#1B3A57', secondaryColor: '#F4A300' },
  { id: 'uns', label: 'Universitas Sebelas Maret', abbrev: 'UNS', fullName: 'Universitas Sebelas Maret', primaryColor: '#003366', secondaryColor: '#CC3333' },
];

const GENERIC_FRAME: TwibbonFrame = {
  id: 'snbp',
  label: 'Lolos SNBP 2026',
  abbrev: 'SNBP',
  fullName: 'Seleksi Nasional Berdasarkan Prestasi',
  primaryColor: '#22C55E',
  secondaryColor: '#FFFFFF',
};

// ---------------------------------------------------------------------------
// Platform format definitions
// ---------------------------------------------------------------------------

interface PlatformFormat {
  id: string;
  label: string;
  labelId: string;
  width: number;
  height: number;
}

const PLATFORM_FORMATS: PlatformFormat[] = [
  { id: 'ig-story', label: 'IG Story', labelId: 'IG Story', width: 1080, height: 1920 },
  { id: 'ig-square', label: 'IG Post', labelId: 'IG Post', width: 1080, height: 1080 },
  { id: 'ig-portrait', label: 'IG Portrait', labelId: 'IG Portrait', width: 1080, height: 1350 },
  { id: 'whatsapp', label: 'WhatsApp', labelId: 'WhatsApp', width: 1080, height: 1080 },
  { id: 'tiktok', label: 'TikTok', labelId: 'TikTok', width: 1080, height: 1920 },
];

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse hex color to RGB components */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

/** Darken a hex color by a factor (0 = black, 1 = original) */
function darken(hex: string, factor: number): string {
  const { r, g, b } = hexToRgb(hex);
  const f = Math.max(0, Math.min(1, factor));
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

// ---------------------------------------------------------------------------
// Canvas rendering — CoC player card aesthetic
// ---------------------------------------------------------------------------

const FONT_FAMILY = '"DM Sans", system-ui, -apple-system, sans-serif';

async function drawFrame(
  ctx: CanvasRenderingContext2D,
  frame: TwibbonFrame,
  photo: HTMLImageElement | null,
  offset: { x: number; y: number },
  scale: number,
  canvasW: number,
  canvasH: number,
  logoImg: HTMLImageElement | null,
) {
  // Ensure DM Sans is loaded
  if (typeof document !== 'undefined') {
    await document.fonts.load(`bold 48px ${FONT_FAMILY}`).catch(() => {});
  }

  // Scale factor relative to reference height (1920 = IG Story)
  const sf = Math.max(canvasH / 1920, 0.75);

  // --- Dark radial gradient background ---
  const { r, g, b } = hexToRgb(frame.primaryColor);
  const radGrad = ctx.createRadialGradient(
    canvasW / 2, canvasH * 0.4, canvasW * 0.1,
    canvasW / 2, canvasH * 0.4, canvasW * 0.9,
  );
  radGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.4)`);
  radGrad.addColorStop(0.5, `rgba(${Math.round(r * 0.2)}, ${Math.round(g * 0.2)}, ${Math.round(b * 0.2)}, 0.9)`);
  radGrad.addColorStop(1, '#0a0a0f');
  ctx.fillStyle = '#0a0a0f';
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.fillStyle = radGrad;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // --- Subtle geometric pattern overlay (diagonal lines) ---
  ctx.save();
  ctx.globalAlpha = 0.04;
  ctx.strokeStyle = frame.secondaryColor;
  ctx.lineWidth = 1;
  const spacing = Math.round(40 * sf);
  for (let i = -canvasH; i < canvasW + canvasH; i += spacing) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + canvasH, canvasH);
    ctx.stroke();
  }
  ctx.restore();

  // --- Draw photo (centered, cover, with offset/scale) ---
  if (photo) {
    const baseScale = Math.max(canvasW / photo.width, canvasH / photo.height);
    const finalScale = baseScale * scale;
    const w = photo.width * finalScale;
    const h = photo.height * finalScale;
    const x = (canvasW - w) / 2 + offset.x;
    const y = (canvasH - h) / 2 + offset.y;
    ctx.drawImage(photo, x, y, w, h);
  }

  // --- Dramatic vignette overlay (radial from transparent center to dark edges) ---
  const vignette = ctx.createRadialGradient(
    canvasW / 2, canvasH * 0.4, canvasW * 0.2,
    canvasW / 2, canvasH * 0.5, canvasW * 0.85,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(0.6, 'rgba(0,0,0,0.3)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.7)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // --- Bottom gradient overlay (stronger, more dramatic) ---
  const gradientH = Math.round(canvasH * 0.35);
  const bottomGrad = ctx.createLinearGradient(0, canvasH - gradientH, 0, canvasH);
  bottomGrad.addColorStop(0, 'rgba(0,0,0,0)');
  bottomGrad.addColorStop(0.3, 'rgba(0,0,0,0.4)');
  bottomGrad.addColorStop(0.6, 'rgba(0,0,0,0.75)');
  bottomGrad.addColorStop(1, 'rgba(0,0,0,0.92)');
  ctx.fillStyle = bottomGrad;
  ctx.fillRect(0, canvasH - gradientH, canvasW, gradientH);

  // --- Top gradient overlay ---
  const topGradH = Math.round(canvasH * 0.15);
  const topGrad = ctx.createLinearGradient(0, 0, 0, topGradH);
  topGrad.addColorStop(0, 'rgba(0,0,0,0.7)');
  topGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(0, 0, canvasW, topGradH);

  // --- Thin metallic border lines along edges ---
  const borderInset = Math.round(12 * sf);
  const borderRadius = Math.round(4 * sf);
  ctx.save();
  ctx.strokeStyle = frame.secondaryColor;
  ctx.globalAlpha = 0.25;
  ctx.lineWidth = Math.round(2 * sf);
  ctx.beginPath();
  ctx.roundRect(borderInset, borderInset, canvasW - borderInset * 2, canvasH - borderInset * 2, borderRadius);
  ctx.stroke();
  ctx.restore();

  // --- Corner glow accents ---
  const glowSize = Math.round(120 * sf);
  const corners = [
    [0, 0],
    [canvasW, 0],
    [0, canvasH],
    [canvasW, canvasH],
  ];
  for (const [cx, cy] of corners) {
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowSize);
    glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.15)`);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(cx - glowSize, cy - glowSize, glowSize * 2, glowSize * 2);
  }

  // --- Floating "MABA 2026" badge/ribbon at top ---
  const bannerMinH = Math.max(canvasH * 0.0625, canvasW * 0.07);
  const badgeH = Math.round(bannerMinH * 1.2);
  const badgeFontSize = Math.round(42 * sf);
  const badgeY = Math.round(30 * sf);

  ctx.font = `bold ${badgeFontSize}px ${FONT_FAMILY}`;
  const badgeTextW = ctx.measureText('MABA 2026').width;
  const badgePadX = Math.round(40 * sf);
  const badgeW = badgeTextW + badgePadX * 2;
  const badgeX = (canvasW - badgeW) / 2;

  // Badge background with metallic/gold accent border
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, Math.round(8 * sf));
  ctx.fill();

  // Metallic border on badge
  ctx.strokeStyle = frame.secondaryColor;
  ctx.lineWidth = Math.round(2.5 * sf);
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, Math.round(8 * sf));
  ctx.stroke();
  ctx.restore();

  // Badge text
  ctx.fillStyle = frame.secondaryColor;
  ctx.font = `bold ${badgeFontSize}px ${FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('MABA 2026', canvasW / 2, badgeY + badgeH / 2);

  // --- Bottom overlay content ---
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Position calculations from bottom
  const logoSize = Math.round(canvasW * 0.15);
  const logoY = canvasH - Math.round(320 * sf);
  const abbrevFontSize = Math.round(80 * sf);
  const fullNameFontSize = Math.round(28 * sf);
  const subtitleFontSize = Math.round(34 * sf);

  const abbrevY = canvasH - Math.round(210 * sf);
  const fullNameY = canvasH - Math.round(165 * sf);
  const subtitleY = canvasH - Math.round(120 * sf);
  const barY = canvasH - Math.round(95 * sf);
  const watermarkY = canvasH - Math.round(55 * sf);

  // University logo
  if (logoImg) {
    const logoX = (canvasW - logoSize) / 2;
    ctx.save();
    ctx.shadowColor = frame.secondaryColor;
    ctx.shadowBlur = Math.round(15 * sf);
    ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
    ctx.restore();
  }

  // University abbreviation (bold display type)
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `900 ${abbrevFontSize}px ${FONT_FAMILY}`;
  ctx.fillText(frame.abbrev, canvasW / 2, abbrevY);

  // Full university name
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = `${fullNameFontSize}px ${FONT_FAMILY}`;
  ctx.fillText(frame.fullName, canvasW / 2, fullNameY);

  // "MAHASISWA BARU 2026" subtitle
  ctx.fillStyle = frame.secondaryColor;
  ctx.font = `600 ${subtitleFontSize}px ${FONT_FAMILY}`;
  const subtitleText = 'MAHASISWA BARU 2026';
  ctx.fillText(subtitleText, canvasW / 2, subtitleY);

  // Decorative bar (width matches subtitle text via measureText)
  const subtitleW = ctx.measureText(subtitleText).width;
  const barW = Math.round(subtitleW * 0.6);
  const barH = Math.round(3 * sf);

  // Metallic gradient bar
  const barGrad = ctx.createLinearGradient((canvasW - barW) / 2, 0, (canvasW + barW) / 2, 0);
  barGrad.addColorStop(0, 'rgba(255,255,255,0)');
  barGrad.addColorStop(0.2, frame.secondaryColor);
  barGrad.addColorStop(0.8, frame.secondaryColor);
  barGrad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = barGrad;
  ctx.fillRect((canvasW - barW) / 2, barY, barW, barH);

  // Watermark at 65% opacity
  ctx.font = `${Math.round(24 * sf)}px ${FONT_FAMILY}`;
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText('almanak.day', canvasW / 2, watermarkY);
}

// ---------------------------------------------------------------------------
// Confetti CSS animation (CSS-only, respects prefers-reduced-motion)
// ---------------------------------------------------------------------------

const CONFETTI_STYLE = `
@keyframes twibbon-confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}
@keyframes twibbon-confetti-sway {
  0%, 100% { margin-left: 0; }
  50% { margin-left: 30px; }
}
.twibbon-confetti-piece {
  position: absolute;
  width: 8px;
  height: 8px;
  top: -10px;
  border-radius: 2px;
  animation: twibbon-confetti-fall 3s ease-in forwards, twibbon-confetti-sway 1.5s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .twibbon-confetti-piece { animation: none; display: none; }
}
`;

const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD', '#FFA07A'];

function ConfettiOverlay() {
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 6 + Math.random() * 6,
  }));

  return (
    <>
      <style>{CONFETTI_STYLE}</style>
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {pieces.map(p => (
          <div
            key={p.id}
            className="twibbon-confetti-piece"
            style={{
              left: p.left,
              animationDelay: p.delay,
              backgroundColor: p.color,
              width: p.size,
              height: p.size,
            }}
          />
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TwibbonGeneratorProps {
  /** Pre-select a university frame */
  universityId?: string;
  lang: string;
}

export function TwibbonGenerator({ universityId, lang }: TwibbonGeneratorProps) {
  const isId = lang === 'id';
  const allFrames = [...UNIVERSITY_FRAMES, GENERIC_FRAME];
  const defaultFrame = universityId
    ? allFrames.find(f => f.id === universityId) || GENERIC_FRAME
    : GENERIC_FRAME;

  const [selectedFrame, setSelectedFrame] = useState<TwibbonFrame>(defaultFrame);
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformFormat>(PLATFORM_FORMATS[0]);
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showPostDownload, setShowPostDownload] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);

  // Pan/zoom state
  const [photoOffset, setPhotoOffset] = useState({ x: 0, y: 0 });
  const [photoScale, setPhotoScale] = useState(1);

  // Logo state
  const [logoImg, setLogoImg] = useState<HTMLImageElement | null>(null);

  const canvasW = selectedPlatform.width;
  const canvasH = selectedPlatform.height;
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const offsetAtDragStart = useRef({ x: 0, y: 0 });
  const rafId = useRef(0);

  // Pinch-to-zoom refs
  const initialPinchDistance = useRef(0);
  const initialPinchScale = useRef(1);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Logo preloading ---
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setLogoImg(img);
    img.onerror = () => setLogoImg(null);
    img.src = `${TWIBBON_LOGOS_BASE}/${selectedFrame.id}.svg`;
    return () => { img.onload = null; img.onerror = null; };
  }, [selectedFrame.id]);

  const redraw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    await drawFrame(ctx, selectedFrame, photo, photoOffset, photoScale, canvasW, canvasH, logoImg);
  }, [selectedFrame, photo, photoOffset, photoScale, canvasW, canvasH, logoImg]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // ---- Pointer handlers for drag-to-pan ----
  const getCanvasScale = useCallback(() => {
    const container = previewRef.current;
    if (!container) return 1;
    return canvasW / container.clientWidth;
  }, [canvasW]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!photo) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    offsetAtDragStart.current = { ...photoOffset };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [photo, photoOffset]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const scale = getCanvasScale();
    const dx = (e.clientX - dragStart.current.x) * scale;
    const dy = (e.clientY - dragStart.current.y) * scale;
    const newOffset = {
      x: offsetAtDragStart.current.x + dx,
      y: offsetAtDragStart.current.y + dy,
    };
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      setPhotoOffset(newOffset);
    });
  }, [getCanvasScale]);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!photo) return;
    e.preventDefault();
    setPhotoScale(prev => Math.min(3, Math.max(1, prev - e.deltaY * 0.001)));
  }, [photo]);

  // ---- Pinch-to-zoom touch handlers ----
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      initialPinchDistance.current = getTouchDistance(e.touches);
      initialPinchScale.current = photoScale;
    }
  }, [photoScale]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      const newScale = initialPinchScale.current * (dist / initialPinchDistance.current);
      setPhotoScale(Math.min(3, Math.max(1, newScale)));
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);
    setShowPostDownload(false);

    if (!file.type.startsWith('image/')) {
      setFileError(isId ? 'File harus berupa gambar (JPG, PNG, dll)' : 'File must be an image (JPG, PNG, etc)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError(isId ? 'Ukuran file maksimal 20MB' : 'Maximum file size is 20MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setPhoto(img);
        setPhotoPreview(reader.result as string);
        setPhotoOffset({ x: 0, y: 0 });
        setPhotoScale(1);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3500);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);

    window._paq?.push(['trackEvent', 'Twibbon', 'Upload', selectedFrame.id]);
  };

  const canvasToBlob = (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      if (!canvas) return reject(new Error('No canvas'));
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, 'image/png');
    });
  };

  const getFilename = () => `maba-2026-${selectedFrame.abbrev.toLowerCase()}-${selectedPlatform.id}.png`;

  const handleDownload = async () => {
    const canvas = canvasRef.current;
    if (!canvas || isExporting) return;

    setIsExporting(true);
    try {
      await redraw();
      const blob = await canvasToBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFilename();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowPostDownload(true);
    } finally {
      setIsExporting(false);
    }

    window._paq?.push(['trackEvent', 'Twibbon', 'Download', selectedFrame.id]);
  };

  const handleShare = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await redraw();
      const blob = await canvasToBlob();
      const file = new File([blob], getFilename(), { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
        setShowPostDownload(true);
        window._paq?.push(['trackEvent', 'Twibbon', 'Share', selectedFrame.id]);
      } else {
        await handleDownload();
      }
    } catch {
      // User cancelled share
    } finally {
      setIsExporting(false);
    }
  };

  const suggestedCaption = isId
    ? `Alhamdulillah, resmi jadi mahasiswa ${selectedFrame.fullName}! 🎓`
    : `Officially a student at ${selectedFrame.fullName}! 🎓`;

  const handleCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(suggestedCaption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  const handleRestart = () => {
    setShowPostDownload(false);
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoOffset({ x: 0, y: 0 });
    setPhotoScale(1);
  };

  // Preview size classes — responsive mobile sizing
  const previewSizeClass = canvasH <= canvasW
    ? 'max-w-[360px]'
    : canvasH <= 1350
      ? 'w-full max-w-[min(80vw,340px)]'
      : 'w-full max-w-[min(80vw,300px)]';

  return (
    <SectionShell className="py-8" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Celebratory wrapper with warm gradient */}
      <div
        className="isolate relative overflow-hidden rounded-2xl p-5 md:p-8 shadow-xl bg-[#0a0a0f]"
        style={{
          background: `linear-gradient(135deg, ${darken(selectedFrame.primaryColor, 0.15)} 0%, #0f0f14 40%, #0f0f14 60%, ${darken(selectedFrame.primaryColor, 0.12)} 100%)`,
        }}
      >
        {showConfetti && <ConfettiOverlay />}

        {/* Subtle glow accent */}
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[500px] rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: selectedFrame.primaryColor }}
          aria-hidden="true"
        />

        <div className="relative z-10">
          <h2 className="font-heading text-2xl font-bold text-white md:text-3xl tracking-tight">
            {isId ? 'Selamat! Kamu diterima!' : 'Congratulations! You got in!'}
          </h2>
          <p className="mt-1.5 text-sm text-white/60">
            {isId
              ? 'Buat twibbon keren dan rayakan bersama teman-teman!'
              : 'Create your twibbon and celebrate with friends!'}
          </p>

          {/* Social proof */}
          <p className="mt-2 text-xs text-white/40 font-medium">
            {isId ? '1,234 MABA sudah buat twibbon' : '1,234 students have created their twibbon'}
          </p>

          <div className="mt-6 grid gap-8 md:grid-cols-[1fr,1.5fr]">
            {/* Controls */}
            <div className="order-last space-y-5 md:order-first">
              {/* Platform selector */}
              <div>
                <label className="mb-2.5 block text-xs font-semibold text-white/50 uppercase tracking-widest">
                  {isId ? 'Pilih Platform' : 'Select Platform'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_FORMATS.map(pf => {
                    const PlatIcon = PLATFORM_ICONS[pf.id];
                    return (
                      <button
                        key={pf.id}
                        onClick={() => {
                          if (pf.id !== selectedPlatform.id) {
                            setSelectedPlatform(pf);
                            setPhotoOffset({ x: 0, y: 0 });
                            setPhotoScale(1);
                          }
                        }}
                        aria-pressed={selectedPlatform.id === pf.id}
                        className={`flex items-center gap-1.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
                          selectedPlatform.id === pf.id
                            ? 'border-white/30 bg-white/10 text-white shadow-lg'
                            : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
                        }`}
                      >
                        {PlatIcon && <PlatIcon className="h-4 w-4" />}
                        {isId ? pf.labelId : pf.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Frame selector — horizontal scroll strip */}
              <div>
                <label className="mb-2.5 block text-xs font-semibold text-white/50 uppercase tracking-widest">
                  {isId ? 'Pilih Frame' : 'Select Frame'}
                </label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {allFrames.map(frame => (
                    <button
                      key={frame.id}
                      onClick={() => setSelectedFrame(frame)}
                      aria-pressed={selectedFrame.id === frame.id}
                      className={`flex flex-shrink-0 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
                        selectedFrame.id === frame.id
                          ? 'border-white/40 bg-white/15 text-white shadow-lg ring-1'
                          : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
                      }`}
                      style={selectedFrame.id === frame.id ? {
                        borderColor: frame.secondaryColor,
                        boxShadow: `0 0 12px ${frame.primaryColor}40`,
                      } : undefined}
                    >
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-white/20 flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${frame.primaryColor} 50%, ${frame.secondaryColor} 50%)`,
                        }}
                      />
                      {frame.abbrev}
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload */}
              <div>
                <label className="mb-2.5 block text-xs font-semibold text-white/50 uppercase tracking-widest">
                  {isId ? 'Upload Foto' : 'Upload Photo'}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-xl border-2 border-dashed border-white/20 p-6 text-center text-sm text-white/60 transition-all hover:border-white/40 hover:text-white/80 hover:bg-white/5"
                >
                  {photoPreview ? (
                    <div className="flex items-center justify-center gap-3">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={photoPreview} alt="" className="h-20 w-20 rounded-lg object-cover ring-2 ring-white/20" />
                      <span>{isId ? 'Ganti foto' : 'Change photo'}</span>
                    </div>
                  ) : (
                    <>
                      <IconCamera className="mx-auto h-8 w-8 mb-2 text-white/40" />
                      {isId ? 'Tap untuk upload selfie' : 'Tap to upload selfie'}
                    </>
                  )}
                </button>
                {fileError && (
                  <p className="mt-1.5 text-xs text-red-400" role="alert">{fileError}</p>
                )}
              </div>

              {/* Download + Share buttons */}
              {!showPostDownload ? (
                <div className="flex gap-3">
                  <button
                    onClick={handleDownload}
                    disabled={!photo || isExporting}
                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-3.5 text-sm font-bold text-gray-900 transition-all hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {isExporting ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {isId ? 'Memproses...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <IconDownload className="h-4 w-4" />
                        Download
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={!photo || isExporting}
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <IconShare className="h-4 w-4" />
                    Share
                  </button>
                </div>
              ) : (
                /* Post-download/share celebration state */
                <div className="space-y-3 rounded-xl border border-white/10 bg-white/5 p-4">
                  <p className="text-sm font-semibold text-white">
                    {isId ? 'Twibbon siap!' : 'Twibbon ready!'}
                  </p>

                  <div>
                    <p className="text-xs text-white/50 mb-1.5">
                      {isId ? 'Caption yang disarankan:' : 'Suggested caption:'}
                    </p>
                    <div className="flex items-start gap-2 rounded-lg bg-white/5 p-3">
                      <p className="flex-1 text-sm text-white/80 italic">{suggestedCaption}</p>
                      <button
                        onClick={handleCopyCaption}
                        className="flex-shrink-0 rounded-md p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label={isId ? 'Salin caption' : 'Copy caption'}
                      >
                        <IconCopy className="h-4 w-4" />
                      </button>
                    </div>
                    {captionCopied && (
                      <p className="mt-1 text-xs text-green-400">{isId ? 'Tersalin!' : 'Copied!'}</p>
                    )}
                  </div>

                  <button
                    onClick={handleRestart}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm font-medium text-white/80 transition-all hover:bg-white/10"
                  >
                    <IconRefresh className="h-4 w-4" />
                    {isId ? 'Buat untuk teman' : 'Create for a friend'}
                  </button>
                </div>
              )}

              <p className="text-xs text-white/30 text-center">
                {isId
                  ? `Ukuran ${canvasW}×${canvasH} — ${selectedPlatform.labelId}`
                  : `Size ${canvasW}×${canvasH} — ${selectedPlatform.label}`}
              </p>
            </div>

            {/* Preview — order-first on mobile so user sees result immediately */}
            <div className="order-first flex flex-col items-center justify-start md:order-last">
              <div
                ref={previewRef}
                className={`${previewSizeClass} cursor-grab overflow-hidden rounded-2xl shadow-2xl active:cursor-grabbing transition-all duration-300`}
                style={{
                  touchAction: 'pan-y',
                  boxShadow: `0 0 40px ${selectedFrame.primaryColor}30, 0 20px 60px rgba(0,0,0,0.5)`,
                }}
                tabIndex={-1}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
              >
                <canvas
                  ref={canvasRef}
                  width={canvasW}
                  height={canvasH}
                  className="w-full"
                  style={{ aspectRatio: `${canvasW}/${canvasH}` }}
                  role="img"
                  aria-label={isId
                    ? `Preview twibbon ${selectedFrame.abbrev} untuk ${selectedPlatform.labelId}`
                    : `${selectedFrame.abbrev} twibbon preview for ${selectedPlatform.label}`}
                />
              </div>
              {photo && (
                <>
                  <p className="mt-3 text-xs text-white/40 text-center">
                    {isId ? 'Geser foto untuk atur posisi • Cubit untuk zoom' : 'Drag to reposition • Pinch to zoom'}
                  </p>
                  <div className={`mt-1.5 flex w-full items-center gap-2 ${previewSizeClass}`}>
                    <span className="text-xs text-white/40">−</span>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={photoScale}
                      onChange={e => setPhotoScale(parseFloat(e.target.value))}
                      className="flex-1 accent-white"
                      aria-label={isId ? 'Zoom foto' : 'Photo zoom'}
                      aria-valuemin={1}
                      aria-valuemax={3}
                      aria-valuenow={Math.round(photoScale * 100) / 100}
                      aria-valuetext={`${Math.round(photoScale * 100)}%`}
                    />
                    <span className="text-xs text-white/40">+</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
